import React, { useState, useEffect, useCallback } from 'react';
import { useFilters } from '../context/FilterContext';
import * as api from '../services/apiService';
import {
  formatIndianCurrency, formatIndianNumber, formatPercent, formatFullIndianCurrency,
  COLORS, STATUS_COLORS,
} from '../utils/formatters';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, ComposedChart, Line, Area, LabelList,
  RadialBarChart, RadialBar, Treemap, FunnelChart, Funnel,
} from 'recharts';
import {
  FileText, IndianRupee, CheckCircle, TrendingUp, Percent, Shield, Calculator, Clock,
  Users, Stethoscope, UserCircle, Building2, UserCheck, AlertTriangle, Baby,
  Zap, Timer, XCircle, FileWarning, MapPin, Activity, Hash, Sparkles, Upload, Loader2,
} from 'lucide-react';

/* ======================== SHARED COMPONENTS ======================== */

const InfoTip = ({ text }) => (
  <span className="relative group ml-1 inline-flex">
    <span className="w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[8px] font-bold cursor-help">i</span>
    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2.5 py-1.5 text-[9px] text-white bg-gray-800 rounded-lg shadow-lg w-52 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 leading-relaxed normal-case">{text}</span>
  </span>
);

const KPI = ({ title, value, sub, icon: Icon, color = '#7C3AED', onClick, active, info }) => (
  <div
    className={`bg-white rounded-xl border shadow-sm p-4 hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:border-purple-400' : ''} ${active ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{title}{info && <InfoTip text={info} />}</p>
        <p className="text-xl font-bold text-gray-900 mt-0.5 truncate">{value}</p>
        {sub && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{sub}</p>}
      </div>
      {Icon && <div className="p-2 rounded-lg flex-shrink-0 ml-2" style={{ backgroundColor: color + '15' }}><Icon size={16} style={{ color }} /></div>}
    </div>
  </div>
);

const BlurredName = (v) => {
  if (!v) return '-';
  // Split into visible chars and masked (*) parts, only blur the * portions
  const parts = String(v).split(/(\*+)/);
  return <span className="select-none">{parts.map((part, i) =>
    part.includes('*')
      ? <span key={i} style={{ filter: 'blur(3px)', WebkitFilter: 'blur(3px)', display: 'inline-block' }}>{part}</span>
      : <span key={i}>{part}</span>
  )}</span>;
};

const SECTION_INFO = {
  'portfolio': 'Overview of claims portfolio - total claims, premium, ICR, and key metrics at a glance',
  'renewal-premium': 'Expected renewal premium range based on claims experience - aggressive to conservative estimates using ICR-based or discount method',
  'settlement': 'Claim settlement analysis - TAT (turnaround time), cashless vs reimbursement split, and settlement rates',
  'ailment': 'Disease-wise claims breakdown - top ailments by claim amount and frequency',
  'maternity': 'Maternity claims analysis - normal vs C-section delivery, metro vs non-metro, and limit utilization',
  'employee': 'Employee-level claims analysis - top claimants, repeat claimants, and high-cost employees',
  'utilization': 'Sum Insured utilization bands - how much of the coverage employees are using',
  'relationship': 'Relationship-wise claims split - self, spouse, children, parents contribution to total claims',
  'demographic': 'Age and gender distribution of claims - helps identify high-risk demographic segments',
  'hospital': 'Hospital-wise claims analysis - top hospitals, network vs non-network, and city distribution',
  'rejection': 'Claims rejection analysis - rejection rate, top rejection reasons, and categories',
  'claims-detail': 'Detailed claims register - searchable table with all individual claim records',
  'policy-benefits': 'Extracted policy benefits from your policy copy - coverage details, limits, and inclusions',
  'ai-insights': 'AI-generated observations and recommendations based on your claims data and policy benchmarks',
  'key-factors': 'What pulls the settlement ratio down - rejections, maternity limit, SI exhausted, co-pay and capped ailments - and the ratio without each',
  'prepost': 'Pre and post hospitalisation claims within reimbursement - how many, how much, and their share',
  'duration': 'Length of stay - hospitalisations, days, average / median / longest stay, by relation and ailment',
  'accidental': 'Accidental claims (ICD codes S, T, V, W, X, Y) - fractures, burns, wounds, falls, injuries and poisonings',
};

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="mb-10">
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200" {...(id === 'health' ? {'data-tour': 'health-section'} : id === 'ai-insights' ? {'data-tour': 'ai-insights'} : {})}>
      {Icon && <Icon size={18} className="text-purple-600" />}
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
      {SECTION_INFO[id] && (
        <span className="relative group ml-1">
          <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[9px] font-bold cursor-help inline-flex">i</span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-[10px] text-white bg-gray-800 rounded-lg shadow-lg w-64 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">{SECTION_INFO[id]}</span>
        </span>
      )}
    </div>
    {children}
  </section>
);

const Card = ({ title, children, className = '', info }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 overflow-visible ${className}`}>
    {title && <h3 className="text-xs font-semibold text-gray-700 mb-3">{title}{info && <InfoTip text={info} />}</h3>}
    {children}
  </div>
);

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2.5 text-[11px] max-w-[220px]">
      <p className="font-semibold text-gray-800 mb-1 truncate">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="truncate">
          {p.name}: {typeof p.value === 'number' && p.value > 100 ? formatIndianCurrency(p.value) : formatIndianNumber(p.value)}
        </p>
      ))}
    </div>
  );
};

const RADIAN = Math.PI / 180;
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>{Math.round(percent * 100)}%</text>;
};

// TAT chart uses Cashless/Reimbursement colors directly
const GENDER_COLORS = { Male: '#663399', Female: '#e6377a', Other: '#F59E0B' };
const REL_COLORS = { Self: '#7C3AED', Spouse: '#EC4899', Parents: '#F59E0B', Children: '#10B981' };

/* ======================== MAIN DASHBOARD ======================== */

const SinglePageDashboard = () => {
  const { filters, drillFilters, setDrillFilter } = useFilters();
  const [ailmentView, setAilmentView] = useState('All');
  const [matCityView, setMatCityView] = useState('All');
  const [dqOpen, setDqOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  const [policyBenefits, setPolicyBenefits] = useState(null);
  const [policyBenefitsLoading, setPolicyBenefitsLoading] = useState(false);
  const [policyUploading, setPolicyUploading] = useState(false);
  const [policyExtractId, setPolicyExtractId] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [rpLivesOverride, setRpLivesOverride] = useState(null);

  // Fetch all data from backend whenever filters change
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const jobId = window.location.pathname.includes('/dashboard/') ? window.location.pathname.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || '' : '';
      const [portfolio, trend, relations, ailments, demographics, hospitals, employees, utilization, maternity, settlement, rejection, claimsDetail] = await Promise.all([
        api.getPortfolio(filters, drillFilters),
        api.getMonthlyTrend(filters, drillFilters),
        api.getRelationship(filters, drillFilters),
        api.getAilments(filters, drillFilters, ailmentView),
        api.getDemographics(filters, drillFilters),
        api.getHospitals(filters, drillFilters),
        api.getEmployees(filters, drillFilters),
        api.getUtilization(filters, drillFilters),
        api.getMaternity(filters, drillFilters),
        api.getSettlement(filters, drillFilters),
        api.getRejections(filters, drillFilters),
        api.getClaimsDetail(filters, drillFilters),
      ]);

      if (portfolio.empty) { setD(null); setLoading(false); return; }

      // Optional sections: a backend without them just leaves them out.
      const [prepost, keyFactors, duration, accidental] = await Promise.all([
        api.getPrepost(filters, drillFilters).catch(() => null),
        api.getKeyFactors(filters, drillFilters).catch(() => null),
        api.getHospitalizationDuration(filters, drillFilters).catch(() => null),
        api.getAccidental(filters, drillFilters).catch(() => null),
      ]);

      setD({
        ...portfolio,
        trend,
        relations,
        topAilAmt: ailments.topByAmount || [],
        topAilCnt: ailments.topByCount || [],
        icdBreakdown: ailments.icdBreakdown || [],
        ailmentTable: ailments.table || [],
        ageGroups: demographics.ageGroups || [],
        gender: demographics.gender || [],
        amountBands: demographics.amountBands || [],
        cityType: demographics.cityType || [],
        topHospAmt: hospitals.topByAmount || [],
        topHospCnt: hospitals.topByCount || [],
        hospType: hospitals.hospType || [],
        hospCity: hospitals.hospCity || [],
        hospTable: hospitals.table || [],
        uniqueHospitals: hospitals.uniqueHospitals || 0,
        uniqueCities: hospitals.uniqueCities || 0,
        topEmpAmt: employees.topByAmount || [],
        topEmpCnt: employees.topByCount || [],
        empTable: employees.table || [],
        highUtil: employees.highUtil || 0,
        utilBands: utilization.bands || [],
        siSplit: utilization.siSplit || [],
        maternity,
        tat: settlement,
        rejection,
        claimsDetail: claimsDetail.claims || [],
        prepost: prepost && !prepost.empty && prepost.total ? prepost : null,
        keyFactors: keyFactors && !keyFactors.empty && keyFactors.rows?.length ? keyFactors : null,
        duration: duration && !duration.empty && duration.totals ? duration : null,
        accidental: accidental && !accidental.empty && accidental.accidental ? accidental.accidental : null,
        policyBenefits: null,
      });
    } catch (err) {
      /* fetch error — dashboard sections will show empty */
    }
    setLoading(false);
  }, [filters, drillFilters, ailmentView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch policy benefits asynchronously after main data loads
  useEffect(() => {
    if (!d) return;
    const jobId = window.location.pathname.includes('/dashboard/') ? window.location.pathname.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || '' : '';
    if (!jobId) return;
    setPolicyBenefitsLoading(true);
    fetch(`${api.API_BASE}/api/policy-benefits?job_id=${jobId}`)
      .then(r => r.json())
      .then(data => { if (data.fields) setPolicyBenefits(data.fields); })
      .catch(() => {})
      .finally(() => setPolicyBenefitsLoading(false));
  }, [d?.totalClaims]);

  // Fetch AI insights asynchronously after main data loads (polls if generating)
  useEffect(() => {
    if (!d) return;
    const jobId = window.location.pathname.includes('/dashboard/') ? window.location.pathname.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || '' : '';
    if (!jobId) return;
    setInsightsLoading(true);
    let active = true;
    const startTime = Date.now();
    const MAX_POLL = 90000; // 90s max
    const fetchInsights = async () => {
      try {
        const r = await fetch(`${api.API_BASE}/api/insights?job_id=${jobId}`);
        const data = await r.json();
        if (!active) return;
        if (data.status === 'ready' && data.insights) {
          setInsights(data.insights);
          setInsightsLoading(false);
          return;
        }
        if (data.status === 'generating' && Date.now() - startTime < MAX_POLL) {
          setTimeout(fetchInsights, 5000); // Poll every 5s
          return;
        }
      } catch {}
      if (active) setInsightsLoading(false);
    };
    fetchInsights();
    return () => { active = false; };
  }, [d?.totalClaims]); // Only re-fetch when main data changes, not on every filter

  // Poll for policy extraction result (timeout after 90s)
  useEffect(() => {
    if (!policyExtractId) return;
    let active = true;
    const startTime = Date.now();
    const MAX_POLL_TIME = 90000; // 90 seconds
    const poll = async () => {
      if (Date.now() - startTime > MAX_POLL_TIME) {
        if (active) {
          setPolicyUploading(false);
          setPolicyExtractId(null);
        }
        return;
      }
      try {
        const r = await fetch(`${api.API_BASE}/api/policy/extract-status/${policyExtractId}`);
        const data = await r.json();
        if (!active) return;
        if (data.status === 'done' && data.fields) {
          setPolicyBenefits(data.fields);
          setPolicyExtractId(null);
          setPolicyUploading(false);
          const jobId = window.location.pathname.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || '';
          if (jobId) {
            fetch(`${api.API_BASE}/api/policy/save-to-job`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ job_id: jobId, policy_extract_id: policyExtractId, policy_benefits: data.fields }),
            }).catch(() => {});
          }
        } else if (data.status === 'error') {
          setPolicyUploading(false);
          setPolicyExtractId(null);
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { active = false; clearInterval(interval); };
  }, [policyExtractId]);

  // Handle policy upload from dashboard via signed URL
  const handleDashboardPolicyUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return;
    setPolicyUploading(true);
    try {
      // Get signed URLs (original + _raw.pdf)
      const signedRes = await fetch(`${api.API_BASE}/api/policy/signed-url?filename=${encodeURIComponent(file.name)}`).then(r => r.json());
      const { upload_url, raw_upload_url, extract_id } = signedRes;
      // Upload to S3
      if (upload_url.startsWith('/api/mock-s3-upload')) {
        const form = new FormData();
        form.append('file', file);
        await fetch(upload_url, { method: 'POST', body: form });
      } else {
        await Promise.all([
          fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } }),
          fetch(raw_upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } }),
        ]);
      }
      // Start extraction
      const res = await fetch(`${api.API_BASE}/api/policy/start-extraction`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extract_id }),
      }).then(r => r.json());
      if (res.extract_id) {
        setPolicyExtractId(res.extract_id);
      } else {
        setPolicyUploading(false);
      }
    } catch {
      setPolicyUploading(false);
    }
  };

  if (loading && !d) return (
    <div className="max-w-7xl mx-auto px-4 py-6 animate-pulse">
      {/* Skeleton KPI row */}
      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
        <div className="w-5 h-5 bg-purple-200 rounded" />
        <div className="h-4 bg-gray-200 rounded w-40" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="h-2.5 bg-gray-200 rounded w-20 mb-3" />
            <div className="h-6 bg-gray-200 rounded w-16 mb-2" />
            <div className="h-2 bg-gray-100 rounded w-24" />
          </div>
        ))}
      </div>
      {/* Skeleton KPI row 2 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="h-2.5 bg-gray-200 rounded w-24 mb-3" />
            <div className="h-6 bg-gray-200 rounded w-20 mb-2" />
            <div className="h-2 bg-gray-100 rounded w-16" />
          </div>
        ))}
      </div>
      {/* Skeleton chart row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[1,2,3].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="h-3 bg-gray-200 rounded w-36 mb-4" />
            <div className="h-48 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
      {/* Skeleton table */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
        <div className="h-3 bg-gray-200 rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="flex gap-4">
              <div className="h-3 bg-gray-100 rounded flex-1" />
              <div className="h-3 bg-gray-100 rounded w-20" />
              <div className="h-3 bg-gray-100 rounded w-24" />
              <div className="h-3 bg-gray-100 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
      {/* Loading indicator */}
      <div className="text-center py-4">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-xs text-gray-400">Loading dashboard data...</p>
      </div>
    </div>
  );

  if (!d) return <EmptyState />;

  const totalRelAmount = (d.relations || []).reduce((a, b) => a + b.amount, 0);
  const totalRelCount = (d.relations || []).reduce((a, b) => a + b.count, 0);
  const livesInception = d.livesInception || 0;
  const livesCurrent = d.livesCurrent || 0;
  const empInception = d.empInception || 0;
  const empCurrent = d.empCurrent || 0;
  const livesPctChange = livesInception > 0 ? (((livesCurrent - livesInception) / livesInception) * 100) : 0;
  const empPctChange = empInception > 0 ? (((empCurrent - empInception) / empInception) * 100) : 0;
  const highUtil = d.highUtil || 0;
  const mat = d.maternity || {};
  const rej = d.rejection || {};

  const toggleClass = (val) => `px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${ailmentView === val ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`;

  // Drill-down helper
  const drill = (column, value) => () => setDrillFilter(column, value);
  const isDrilled = (column, value) => drillFilters[column] === value;
  const onChartClick = (column) => (data) => {
    if (data && data.name) setDrillFilter(column, data.name);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6 relative">
      {/* Loading bar when refreshing with drill-down */}
      {loading && d && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-purple-100">
          <div className="h-full bg-purple-600 animate-pulse" style={{ width: '60%' }} />
        </div>
      )}


      {/* ====== DATA QUALITY ALERT ====== */}
      {(() => {
        const dq = d.dataQuality || [];
        if (!dq.length) return null;
        const critical = dq.filter(f => f.severity === 'critical');
        const important = dq.filter(f => f.severity === 'important');
        const info = dq.filter(f => f.severity === 'info');
        const sevBadge = (s) => s === 'critical' ? 'bg-red-100 text-red-700' : s === 'important' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-600';
        const sevDot = (s) => s === 'critical' ? 'bg-red-500' : s === 'important' ? 'bg-amber-500' : 'bg-blue-400';
        return (
          <div className={`mb-4 rounded-xl border ${critical.length > 0 ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
            <button onClick={() => setDqOpen(!dqOpen)} className="w-full flex items-center justify-between px-4 py-3 text-left">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className={critical.length > 0 ? 'text-red-500' : 'text-amber-500'} />
                <span className="text-xs font-semibold text-gray-800">
                  Data Quality: {critical.length > 0 && <span className="text-red-600">{critical.length} critical</span>}
                  {critical.length > 0 && important.length > 0 && ', '}
                  {important.length > 0 && <span className="text-amber-600">{important.length} warnings</span>}
                  {info.length > 0 && <span className="text-gray-500"> + {info.length} info</span>}
                </span>
                <span className="text-[10px] text-gray-400">— these may affect dashboard accuracy</span>
              </div>
              <span className="text-xs text-gray-400">{dqOpen ? '▲ Hide' : '▼ View Details'}</span>
            </button>
            {dqOpen && (
              <div className="px-4 pb-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-1.5 text-gray-500 font-semibold">Severity</th>
                      <th className="text-left py-1.5 text-gray-500 font-semibold">Field</th>
                      <th className="text-left py-1.5 text-gray-500 font-semibold">Issue</th>
                      <th className="text-right py-1.5 text-gray-500 font-semibold">Affected</th>
                      <th className="text-left py-1.5 text-gray-500 font-semibold pl-3">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dq.map((f, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-1.5"><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${sevBadge(f.severity)}`}><span className={`w-1.5 h-1.5 rounded-full ${sevDot(f.severity)}`} />{f.severity}</span></td>
                        <td className="py-1.5 font-medium text-gray-800">{f.field}</td>
                        <td className="py-1.5 text-gray-600">{f.issue}</td>
                        <td className="py-1.5 text-right font-semibold text-gray-800">{f.count} <span className="text-gray-400">({f.pct}%)</span></td>
                        <td className="py-1.5 text-gray-500 pl-3 text-[10px]">{f.impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}


      {/* ====== POLICY HEALTH SUMMARY - color-coded metrics ====== */}
      {(() => {
        const icr = d.icrAnnualized || 0;
        const totalCl = d.totalClaims || 1;
        const reimbPct = d.typeSplit?.reduce((s, t) => t.name?.toLowerCase() === 'reimbursement' ? (t.count / totalCl * 100) : s, 0) || 0;

        const crossTab = d.statusTypeCrossTab || [];
        const settledAmt = crossTab.filter(r => r.status === 'Settled').reduce((s, r) => s + (r.incurred || r.amount || 0), 0);
        const closedAmt = crossTab.filter(r => r.status === 'Settled' || r.status === 'Rejected').reduce((s, r) => s + (r.amount || 0), 0);
        const settlePct = closedAmt > 0 ? (settledAmt / closedAmt * 100) : 0;

        const tatBuckets = d.tat?.tatBuckets || [];
        const totalReimbSettled = tatBuckets.reduce((s, b) => s + (b.Reimbursement || 0), 0);
        const over30 = tatBuckets.filter(b => b.name?.includes('30') || b.name?.includes('45') || b.name?.includes('+')).reduce((s, b) => s + (b.Reimbursement || 0), 0);
        const tatPct = totalReimbSettled > 0 ? (over30 / totalReimbSettled * 100) : null;

        const utilBands = (d.utilBands || []).filter(b => !b.isTotal);
        const highBands = utilBands.filter(b => b.name?.startsWith('80') || b.name?.startsWith('>=') || b.name?.startsWith('>100') || b.name === '100%');
        const siPct = highBands.reduce((s, b) => s + parseFloat(String(b.pctIncurred || '0').replace('%', '')), 0);

        const mat = d.maternity || {};
        const matTotal = mat.totalMaternity || 0;
        const matExceed = mat.exceedLimitCount || 0;
        const matPct = matTotal > 0 ? (matExceed / matTotal * 100) : null;

        const relTotal = (d.relations || []).reduce((s, r) => s + (r.amount || 0), 0) || 1;
        const parentAmt = (d.relations || []).find(r => r.name?.toLowerCase() === 'parents')?.amount || 0;
        const parPct = parentAmt > 0 ? (parentAmt / relTotal * 100) : null;

        const getColor = (val, thresholds) => {
          if (thresholds.type === 'icr') {
            if (val >= 80 && val <= 105) return { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', dot: 'bg-emerald-500' };
            if (val < 80) return { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', dot: 'bg-amber-500' };
            return { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', dot: 'bg-red-500' };
          }
          if (thresholds.highIsBad) {
            return val > thresholds.limit
              ? { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', dot: 'bg-red-500' }
              : { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', dot: 'bg-emerald-500' };
          }
          return val < thresholds.limit
            ? { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', dot: 'bg-red-500' }
            : { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', dot: 'bg-emerald-500' };
        };

        const metrics = [];
        if (icr > 0) metrics.push({ value: `${Math.round(icr)}%`, label: 'ICR (Annualized)', desc: icr > 105 ? 'Policy Bleeding' : icr < 80 ? 'Low Utilisation' : 'Healthy Range', info: 'Incurred Claims Ratio annualized to 365 days. Above 105% means claims exceed premium - policy is loss-making. Below 80% indicates low utilization.', ...getColor(icr, { type: 'icr' }) });
        if (d.typeSplit?.length > 1) metrics.push({ value: `${Math.round(reimbPct)}%`, label: 'Reimbursement Claims', desc: reimbPct > 20 ? 'High Reimbursement' : 'Healthy', info: 'Percentage of claims filed as reimbursement vs cashless. High reimbursement indicates employees are going to non-network hospitals or facing cashless denial.', ...getColor(reimbPct, { highIsBad: true, limit: 20 }) });
        if (closedAmt > 0) metrics.push({ value: `${Math.round(settlePct)}%`, label: 'Settlement Ratio (Value)', desc: settlePct < 85 ? 'Low Settlement' : 'Good Settlement', info: 'Ratio of settled amount to total closed claims amount. Below 85% means significant deductions by insurer/TPA - review claim processing quality.', ...getColor(settlePct, { highIsBad: false, limit: 85 }) });
        if (tatPct !== null) metrics.push({ value: `${Math.round(tatPct)}%`, label: 'Reimb TAT > 30 days', desc: tatPct > 20 ? 'High TAT' : 'Healthy TAT', info: 'Percentage of reimbursement claims taking more than 30 days to settle. Above 20% indicates TPA processing delays - escalate with insurer.', ...getColor(tatPct, { highIsBad: true, limit: 20 }) });
        if (utilBands.length > 0) metrics.push({ value: `${Math.round(siPct)}%`, label: 'SI Utilisation > 80%', desc: siPct > 15 ? 'High Utilisation' : 'Healthy', info: 'Percentage of employees who have used more than 80% of their Sum Insured. Above 15% indicates SI may need to be increased at renewal.', ...getColor(siPct, { highIsBad: true, limit: 15 }) });
        if (matPct !== null) metrics.push({ value: `${Math.round(matPct)}%`, label: 'Maternity > Coverage', desc: matPct > 10 ? 'Inadequate Coverage' : 'Adequate', info: 'Percentage of maternity claims where the claimed amount exceeds the policy maternity sub-limit. High value means employees are paying out-of-pocket - consider increasing maternity limits.', ...getColor(matPct, { highIsBad: true, limit: 10 }) });
        if (parPct !== null) metrics.push({ value: `${Math.round(parPct)}%`, label: 'Parental Claims (Value)', desc: parPct > 40 ? 'High Parental' : 'Moderate', info: 'Percentage of total incurred amount from parental (mother/father) claims. Above 40% means parents are driving claim costs - consider co-pay or separate SI for parents.', ...getColor(parPct, { highIsBad: true, limit: 40 }) });

        const row1 = metrics.slice(0, 3);
        const row2 = metrics.slice(3);

        const MetricCard = ({ m }) => (
          <div className={`${m.bg} border-l-4 ${m.border} rounded-xl p-4 shadow-sm`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${m.dot}`} />
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{m.label}</span>
              {m.info && (
                <span className="relative group ml-auto">
                  <span className="w-3.5 h-3.5 rounded-full bg-gray-200/70 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[8px] font-bold cursor-help">i</span>
                  <span className="absolute bottom-full right-0 mb-1.5 px-2.5 py-1.5 text-[9px] text-white bg-gray-800 rounded-lg shadow-lg w-56 text-left opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 leading-relaxed normal-case">{m.info}</span>
                </span>
              )}
            </div>
            <p className={`text-2xl font-bold ${m.text}`}>{m.value}</p>
            <p className="text-[10px] text-gray-500 mt-1">{m.desc}</p>
          </div>
        );

        if (metrics.length === 0) return null;

        return (
          <Section id="health" title="Policy Health Summary" icon={Shield}>
            <div className={`grid gap-3 mb-3 ${row1.length >= 3 ? 'grid-cols-3' : row1.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {row1.map((m, i) => <MetricCard key={i} m={m} />)}
            </div>
            {row2.length > 0 && (
              <div className={`grid gap-3 mb-2 ${row2.length >= 4 ? 'grid-cols-4' : row2.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {row2.map((m, i) => <MetricCard key={i} m={m} />)}
              </div>
            )}
          </Section>
        );
      })()}

      {/* ====== AI OBSERVATIONS & RECOMMENDATIONS (between Health & Portfolio) ====== */}
      {insights && insights.observations && insights.observations.length > 0 && (
        <Section id="ai-insights" title="AI Observations & Recommendations" icon={Sparkles}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {insights.observations.map((obs, i) => {
              const statusColor = obs.status === 'red' ? 'border-red-400 bg-red-50' : obs.status === 'green' ? 'border-green-400 bg-green-50' : 'border-amber-400 bg-amber-50';
              const dotColor = obs.status === 'red' ? 'bg-red-500' : obs.status === 'green' ? 'bg-green-500' : 'bg-amber-500';
              return (
                <div key={i} className={`rounded-xl border-l-4 ${statusColor} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">{obs.topic}</h4>
                  </div>
                  <p className="text-[11px] text-gray-700 mb-1.5">{obs.observation}</p>
                  <p className="text-[10px] text-gray-500 italic">{obs.recommendation}</p>
                </div>
              );
            })}
          </div>
        </Section>
      )}
      {insightsLoading && (
        <Section id="ai-insights" title="AI Observations & Recommendations" icon={Sparkles}>
          <div className="space-y-4 animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 h-28">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="h-2 bg-gray-100 rounded w-full mb-2" />
                  <div className="h-2 bg-gray-100 rounded w-4/5" />
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ====== 1. PORTFOLIO SUMMARY ====== */}
      <Section id="portfolio" title="Portfolio Summary" icon={TrendingUp}>
        {/* Row 1: Core numbers */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
          <KPI title="No. of Claims" value={formatIndianNumber(d.totalClaims)} sub={`${d.uniqueEmps || 0} employees`} icon={FileText} color="#663399" info="Total number of claims filed during the policy period" />
          <KPI title="Total Claimed" value={formatIndianCurrency(d.totalClaimed)} icon={IndianRupee} color="#3B82F6" info="Total amount claimed by employees across all claims" />
          <KPI title="Incurred Amount" value={formatIndianCurrency(d.totalIncurred)} sub="Settled + Outstanding" icon={TrendingUp} color="#F59E0B" info="Total amount paid/approved by insurer (settled + outstanding)" />
          <KPI title="Days Elapsed" value={d.elapsedDays || '-'} sub="Policy period" icon={Clock} info="Number of days elapsed since policy inception" />
          <KPI title="Avg TAT" value={`${d.avgTAT} days`} sub={`Cashless: ${d.tat?.avgCashlessTAT || 0} D | Reimb: ${d.tat?.avgReimbursementTAT || 0} D`} icon={Timer} color="#F59E0B" info="Average turnaround time in days from claim filing to settlement" />
        </div>

        {/* Row 2: ICR gauges + Premium + Lives/Employees */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
          <KPI title="ICR (As on)" value={formatPercent(d.icr)} sub="Incurred Amt / Premium" icon={Percent} color="#EF4444" info="Incurred Claims Ratio - total incurred amount as % of premium paid. Healthy range: 80-105%" />
          <KPI title="ICR (Annualized)" value={formatPercent(d.icrAnnualized)} sub="(Incurred×365/Elapsed) / Premium" icon={TrendingUp} color="#F97316" info="Projected ICR for full year based on elapsed days. Helps predict renewal premium" />
          <KPI title="Premium" value={formatIndianCurrency(d.totalPremium)} sub={d.premiumInception > 0 ? `Inception: ${formatIndianCurrency(d.premiumInception)}` : undefined} icon={Shield} color="#663399" info="Total premium paid for the policy (excluding GST)" />
          {/* Lives + Employees combined card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow col-span-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Lives</p>
                <p className="text-xl font-bold text-gray-900">{livesCurrent > 0 ? formatIndianNumber(livesCurrent) : livesInception > 0 ? formatIndianNumber(livesInception) : '-'}</p>
                {livesCurrent > 0 && livesInception > 0 ? (
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    <span className="text-gray-500">Inception: {formatIndianNumber(livesInception)}</span>
                    <span className={`ml-1 font-semibold ${livesPctChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      ({livesPctChange >= 0 ? '+' : ''}{livesPctChange.toFixed(1)}%)
                    </span>
                  </p>
                ) : livesCurrent === 0 && livesInception > 0 ? (
                  <p className="text-[10px] text-gray-400 mt-0.5">At inception</p>
                ) : null}
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Employees</p>
                <p className="text-xl font-bold text-gray-900">{empCurrent > 0 ? formatIndianNumber(empCurrent) : empInception > 0 ? formatIndianNumber(empInception) : '-'}</p>
                {empCurrent > 0 && empInception > 0 ? (
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    <span className="text-gray-500">Inception: {formatIndianNumber(empInception)}</span>
                    <span className={`ml-1 font-semibold ${empPctChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      ({empPctChange >= 0 ? '+' : ''}{empPctChange.toFixed(1)}%)
                    </span>
                  </p>
                ) : empCurrent === 0 && empInception > 0 ? (
                  <p className="text-[10px] text-gray-400 mt-0.5">At inception</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Status - donut makes sense for composition */}
          <Card title="Claim Status Distribution" info="Breakdown of claims by status - Settled, Rejected, Outstanding">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.statusDist} cx="50%" cy="50%" outerRadius={80} innerRadius={42} dataKey="count" nameKey="name" labelLine={false} label={PieLabel} onClick={onChartClick('Claim Status')} style={{ cursor: 'pointer' }}>
                  {d.statusDist.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || COLORS[i]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10, cursor: 'pointer' }} onClick={(e) => setDrillFilter('Claim Status', e.value)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Cashless vs Reimbursement - donut for proportion */}
          <Card title="Cashless vs Reimbursement (% Claims)" info="Split between cashless (hospital direct) and reimbursement (employee pays first) claims">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.typeSplit} cx="50%" cy="50%" outerRadius={80} innerRadius={42} dataKey="count" nameKey="name" labelLine={false} label={PieLabel} onClick={onChartClick('Claim Type')} style={{ cursor: 'pointer' }}>
                  {d.typeSplit.map((e, i) => <Cell key={i} fill={i === 0 ? '#663399' : '#e6377a'} />)}
                </Pie>
                <Tooltip formatter={v => formatIndianNumber(v) + ' claims'} />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10, cursor: 'pointer' }} onClick={(e) => setDrillFilter('Claim Type', e.value)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Monthly trend - line chart is essential for time series */}
          <Card title="Monthly Claims Trend" info="Month-wise claims count and amount trend over the policy period">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={d.trend} margin={{ top: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 8 }} angle={-35} textAnchor="end" height={45} />
                <YAxis yAxisId="c" orientation="left" tick={{ fontSize: 9 }} />
                <YAxis yAxisId="a" orientation="right" tick={{ fontSize: 9 }} tickFormatter={v => formatIndianCurrency(v)} />
                <Tooltip content={<Tip />} />
                <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
                <Area yAxisId="a" type="monotone" dataKey="amount" name="Claimed Amount" fill="#66339920" stroke="#663399" strokeWidth={2} />
                <Line yAxisId="c" type="monotone" dataKey="count" name="Claims Count" stroke="#e6377a" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DataTable
            title="Claims by Type (Cashless / Reimbursement)"
            info="Breakdown of claims by type showing count, claimed amount, incurred amount, and percentage"
            filename="claims_by_type.csv"
            columns={[
              { key: 'name', header: 'Claim Type' },
              { key: 'count', header: 'No. of Claims', render: v => formatIndianNumber(v) },
              { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'pctCount', header: '% of Claims', render: v => v },
            ]}
            data={d.typeSplit.map(t => ({ ...t, pctCount: d.totalClaims > 0 ? formatPercent((t.count / d.totalClaims) * 100) : '0%' }))}
            onRowClick={(row) => setDrillFilter('Claim Type', row.name)}
          />
          <DataTable
            title="Claims by Status (Paid / Outstanding / Rejected)"
            info="Breakdown of claims by settlement status showing count, amounts, and percentage"
            filename="claims_by_status.csv"
            columns={[
              { key: 'name', header: 'Claim Status' },
              { key: 'count', header: 'No. of Claims', render: v => formatIndianNumber(v) },
              { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'pctCount', header: '% of Claims', render: v => v },
            ]}
            data={[...d.statusDist.map(t => ({ ...t, pctCount: d.totalClaims > 0 ? formatPercent((t.count / d.totalClaims) * 100) : '0%' })), { name: 'Total', count: d.totalClaims, amount: d.totalClaimed, incurred: d.totalIncurred, pctCount: '100%' }]}
            onRowClick={(row) => setDrillFilter('Claim Status', row.name)}
          />
          {/* Waterfall Chart - Cashless + Reimbursement stacked */}
          {(() => {
            const crossTab = d.statusTypeCrossTab || [];
            const getAmt = (status, type) => (crossTab.find(r => r.status === status && r.type === type)?.amount || 0);
            const getInc = (status, type) => (crossTab.find(r => r.status === status && r.type === type)?.incurred || 0);

            const subCashless = getAmt('Settled', 'Cashless') + getAmt('Rejected', 'Cashless') + getAmt('Outstanding', 'Cashless');
            const subReimb = getAmt('Settled', 'Reimbursement') + getAmt('Rejected', 'Reimbursement') + getAmt('Outstanding', 'Reimbursement');
            const submitted = subCashless + subReimb;

            const outCashless = getAmt('Outstanding', 'Cashless');
            const outReimb = getAmt('Outstanding', 'Reimbursement');
            const outTotal = outCashless + outReimb;

            const rejCashless = getAmt('Rejected', 'Cashless');
            const rejReimb = getAmt('Rejected', 'Reimbursement');
            const rejTotal = rejCashless + rejReimb;

            const settCashless = getInc('Settled', 'Cashless');
            const settReimb = getInc('Settled', 'Reimbursement');
            const settTotal = settCashless + settReimb;

            const dedCashless = getAmt('Settled', 'Cashless') - settCashless;
            const dedReimb = getAmt('Settled', 'Reimbursement') - settReimb;
            const dedTotal = Math.max(0, dedCashless + dedReimb);

            const osApprCashless = getInc('Outstanding', 'Cashless');
            const osApprReimb = getInc('Outstanding', 'Reimbursement');

            // Flow: Submitted → what happened to it → what's the result
            // Submitted = Outstanding + Rejected + Settled (claimed)
            // Settled (claimed) = Deduction + Settled (incurred)
            const waterfallData = [
              { name: 'Submitted', base: 0, cashless: subCashless, reimb: subReimb, total: submitted },
              { name: 'Rejection', base: submitted - rejTotal, cashless: rejCashless, reimb: rejReimb, total: rejTotal },
              { name: 'Deduction', base: settTotal + outTotal, cashless: Math.max(0, dedCashless), reimb: Math.max(0, dedReimb), total: dedTotal },
              { name: 'Outstanding', base: settTotal, cashless: outCashless, reimb: outReimb, total: outTotal },
              { name: 'O/S Approved', base: settTotal, cashless: osApprCashless, reimb: osApprReimb, total: osApprCashless + osApprReimb },
              { name: 'Settled', base: 0, cashless: settCashless, reimb: settReimb, total: settTotal },
            ];

            return (
              <Card title="Claims Waterfall (Value)" info="Waterfall showing how submitted claims flow into rejections, deductions, outstanding, and settled amounts" className="mb-4">
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={waterfallData} barSize={60} margin={{ top: 25, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 9 }} tickFormatter={v => formatIndianCurrency(v)} />
                    <Tooltip formatter={v => formatFullIndianCurrency(v)} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="base" stackId="a" fill="transparent" legendType="none" />
                    <Bar dataKey="cashless" stackId="a" name="Cashless" fill="#e6377a" radius={[0, 0, 0, 0]}>
                      <LabelList dataKey="cashless" position="inside" style={{ fontSize: 8, fill: '#fff', fontWeight: 600 }} formatter={v => v > 0 ? formatIndianCurrency(v) : ''} />
                    </Bar>
                    <Bar dataKey="reimb" stackId="a" name="Reimbursement" fill="#663399" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="reimb" position="inside" style={{ fontSize: 8, fill: '#fff', fontWeight: 600 }} formatter={v => v > 0 ? formatIndianCurrency(v) : ''} />
                      <LabelList dataKey="total" position="top" style={{ fontSize: 9, fill: '#333', fontWeight: 700 }} formatter={v => v > 0 ? formatIndianCurrency(v) : ''} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            );
          })()}

          {/* Status × Type Cross-Tab */}
          {(() => {
            const crossTab = d.statusTypeCrossTab || [];
            if (!crossTab.length) return null;
            const statusOrder = ['Settled', 'Rejected', 'Outstanding'];
            const rows = [];
            statusOrder.forEach(status => {
              const types = crossTab.filter(r => r.status === status);
              types.forEach((t, i) => {
                rows.push({ status: i === 0 ? status : '', type: t.type, count: t.count, amount: t.amount, incurred: t.incurred || 0 });
              });
            });
            const totalCount = crossTab.reduce((s, r) => s + (r.count || 0), 0);
            const totalAmt = crossTab.reduce((s, r) => s + (r.amount || 0), 0);
            const totalInc = crossTab.reduce((s, r) => s + (r.incurred || 0), 0);
            rows.push({ status: 'Total', type: '', count: totalCount, amount: totalAmt, incurred: totalInc });
            return (
              <DataTable
                title="Claims Summary (Status × Type)"
                info="Cross-tabulation of claim status and type - shows settlement patterns for cashless vs reimbursement"
                filename="claims_status_type.csv"
                columns={[
                  { key: 'status', header: 'Status', className: 'font-semibold' },
                  { key: 'type', header: 'Type' },
                  { key: 'count', header: 'No. of Claims', render: v => formatIndianNumber(v) },
                  { key: 'amount', header: 'Claim Amount', render: v => formatFullIndianCurrency(v) },
                  { key: 'incurred', header: 'Incurred Amount', render: v => v ? formatFullIndianCurrency(v) : '-' },
                ]}
                data={rows}
              />
            );
          })()}
        </div>
      </Section>

      {/* ====== 2. SETTLEMENT TAT ====== */}
      <Section id="settlement" title="Claim Settlement Analysis" icon={Clock}>
        {/* Settlement Ratio - Type wise first */}
        {(() => {
          const crossTab = d.statusTypeCrossTab || [];
          if (!crossTab.length) return null;
          const types = ['Cashless', 'Reimbursement'];
          const ratioData = types.map(type => {
            const settled = crossTab.find(r => r.status === 'Settled' && r.type === type) || {};
            const rejected = crossTab.find(r => r.status === 'Rejected' && r.type === type) || {};
            const outstanding = crossTab.find(r => r.status === 'Outstanding' && r.type === type) || {};
            const closedCount = (settled.count || 0) + (rejected.count || 0);
            const settledCount = settled.count || 0;
            const settledPct = closedCount > 0 ? Math.round(settledCount / closedCount * 100) : 0;
            const claimAmt = (settled.amount || 0) + (rejected.amount || 0);
            const settledAmt = settled.incurred || settled.amount || 0;
            const settledAmtPct = claimAmt > 0 ? Math.round(settledAmt / claimAmt * 100) : 0;
            return { type, closedCount, settledCount, settledPct, claimAmt, settledAmt, settledAmtPct };
          });
          const totalClosed = ratioData.reduce((s, r) => s + r.closedCount, 0);
          const totalSettled = ratioData.reduce((s, r) => s + r.settledCount, 0);
          const totalPct = totalClosed > 0 ? Math.round(totalSettled / totalClosed * 100) : 0;
          const totalAmt = ratioData.reduce((s, r) => s + r.claimAmt, 0);
          const totalSettledAmt = ratioData.reduce((s, r) => s + r.settledAmt, 0);
          const totalSettledAmtPct = totalAmt > 0 ? Math.round(totalSettledAmt / totalAmt * 100) : 0;

          return (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <KPI title="Settlement Rate (Value)" value={`${totalSettledAmtPct}%`} icon={CheckCircle} color={totalSettledAmtPct >= 85 ? '#10B981' : '#EF4444'} info="Percentage of closed claims that were settled (not rejected)" />
                <KPI title="Cashless Settlement (Value)" value={`${ratioData[0]?.settledAmtPct || 0}%`} icon={Zap} color="#663399" info="Settlement rate by value for cashless claims" />
                <KPI title="Reimbursement Settlement (Value)" value={`${ratioData[1]?.settledAmtPct || 0}%`} icon={Timer} color="#e6377a" info="Settlement rate by value for reimbursement claims" />
                <KPI title="Total Settled Claims" value={formatIndianNumber(totalSettled)} icon={Activity} color="#3B82F6" info="Total number of claims settled by the insurer" />
              </div>
              <DataTable
                title="Settlement Ratio by Claim Type"
                info="Settlement rate comparison between cashless and reimbursement claims by count and value"
                filename="settlement_ratio.csv"
                columns={[
                  { key: 'type', header: 'Claim Type' },
                  { key: 'closedCount', header: 'Closed Claims (Settled + Rejected)', render: v => formatIndianNumber(v) },
                  { key: 'settledCount', header: 'Settled Claims', render: v => formatIndianNumber(v) },
                  { key: 'settledPct', header: '% Settled (Num)', render: v => `${v}%` },
                  { key: 'claimAmt', header: 'Claim Amount', render: v => formatFullIndianCurrency(v) },
                  { key: 'settledAmt', header: 'Settled Amount', render: v => formatFullIndianCurrency(v) },
                  { key: 'settledAmtPct', header: '% Settled (Value)', render: v => `${v}%` },
                ]}
                data={[...ratioData, { type: 'Total', closedCount: totalClosed, settledCount: totalSettled, settledPct: totalPct, claimAmt: totalAmt, settledAmt: totalSettledAmt, settledAmtPct: totalSettledAmtPct }]}
              />
            </>
          );
        })()}

        {/* TAT Analysis */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 mt-6">
          <KPI title="Avg TAT" value={`${d.tat.avgTAT} days`} icon={Clock} info="Average days taken to settle a claim" />
          <KPI title="Cashless TAT" value={`${d.tat.avgCashlessTAT} days`} icon={Zap} color="#10B981" onClick={drill('Claim Type', 'Cashless')} active={isDrilled('Claim Type', 'Cashless')} info="Average settlement time for cashless claims" />
          <KPI title="Reimbursement TAT" value={`${d.tat.avgReimbursementTAT} days`} icon={Timer} color="#F59E0B" onClick={drill('Claim Type', 'Reimbursement')} active={isDrilled('Claim Type', 'Reimbursement')} info="Average settlement time for reimbursement claims" />
          <KPI title="Total Settled" value={formatIndianNumber((d.tat?.tatBuckets || []).reduce((s, b) => s + (b.count || 0), 0))} icon={Activity} color="#3B82F6" info="Total number of claims settled by the insurer" />
        </div>

        <Card title="TAT Distribution - Cashless vs Reimbursement" info="Distribution of claims across turnaround time bands (0-7 days, 7-15 days, etc.)" className="mb-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={(d.tat?.tatBuckets || [])} barGap={2} margin={{ top: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip content={<Tip />} />
              <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Cashless" name="Cashless" fill="#663399" radius={[3, 3, 0, 0]} barSize={24}>
                <LabelList dataKey="Cashless" position="top" style={{ fontSize: 8, fill: '#663399' }} />
              </Bar>
              <Bar dataKey="Reimbursement" name="Reimbursement" fill="#e6377a" radius={[3, 3, 0, 0]} barSize={24}>
                <LabelList dataKey="Reimbursement" position="top" style={{ fontSize: 8, fill: '#e6377a' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <DataTable
          title="TAT Bucket Details"
          info="Distribution of claims across turnaround time bands - helps identify settlement speed patterns"
          filename="tat_details.csv"
          columns={[
            { key: 'name', header: 'TAT Bucket' },
            { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
            { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'settled', header: 'Settled Amount', render: v => formatFullIndianCurrency(v) },
          ]}
          data={d.tat.tatBuckets}
        />
      </Section>

      {d.keyFactors && (
        <Section id="key-factors" title="Key Factors Impacting Settlement Ratio" icon={Percent}>
          <DataTable
            title="Deductions and their impact"
            info="Deducted amount ÷ claimed amount of closed claims is the impact; the adjusted ratio is the settlement ratio without that factor"
            filename="key_factors.csv"
            columns={[
              { key: 'metric', header: 'Metric' },
              { key: 'deductedAmount', header: 'Deducted Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'impactPct', header: 'Impact on Settlement Ratio', render: v => `${Math.round(v)}%` },
              { key: 'adjustedRatioPct', header: 'Adjusted Settlement Ratio', render: v => `${Math.round(v)}%` },
            ]}
            data={d.keyFactors.rows}
          />
          <p className="text-[11px] text-gray-500 mt-2">
            Settlement ratio (value) is {Math.round(d.keyFactors.settlementRatioPct || 0)}%. Policy terms used — cataract cap: {d.keyFactors.assumptions?.cataractCap || '—'}; hernia cap: {d.keyFactors.assumptions?.herniaCap || '—'}; co-pay: {d.keyFactors.assumptions?.copay || '—'}. Others = deductions not explained by the factors above.
          </p>
        </Section>
      )}

      {d.prepost && (() => {
        const rows = [...d.prepost.rows, d.prepost.total];
        const pp = d.prepost.rows.find(r => r.claimType === 'Prepost') || {};
        const reimb = d.prepost.rows.find(r => r.claimType === 'Reimbursement') || {};
        const allReimb = (reimb.count || 0) + (pp.count || 0);
        return (
          <Section id="prepost" title="Impact of Pre & Post Hospitalization on Reimbursement Claims" icon={Activity}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <KPI title="Prepost Claims" value={formatIndianNumber(pp.count || 0)} sub={`${Math.round(pp.shareCount || 0)}% of all claims`} icon={FileText} color="#e6377a" />
              <KPI title="Prepost Amount" value={formatIndianCurrency(pp.claimedAmount || 0)} sub={`${Math.round(pp.shareValue || 0)}% of claimed value`} icon={IndianRupee} color="#663399" />
              <KPI title="Share of Reimbursement" value={d.prepost.prepostOfReimbursementPct != null ? `${Math.round(d.prepost.prepostOfReimbursementPct)}%` : (allReimb ? `${Math.round((pp.count || 0) / allReimb * 100)}%` : '—')} sub="Reimbursement claims that are Prepost" icon={Percent} color="#F59E0B" />
              <KPI title="Avg Prepost Claim" value={pp.count ? formatIndianCurrency(Math.round(pp.claimedAmount / pp.count)) : '—'} sub={reimb.count ? `vs ${formatIndianCurrency(Math.round(reimb.claimedAmount / reimb.count))} reimbursement` : ''} icon={Activity} color="#3B82F6" />
            </div>
            <DataTable
              title="Claims by Type"
              filename="prepost_impact.csv"
              columns={[
                { key: 'claimType', header: 'Claim Type' },
                { key: 'count', header: 'No. of claims', render: v => formatIndianNumber(v) },
                { key: 'claimedAmount', header: 'Claim Amount', render: v => formatFullIndianCurrency(v) },
                { key: 'incurredAmount', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
                { key: 'shareCount', header: '% Share (Num)', render: v => `${Math.round(v)}%` },
                { key: 'shareValue', header: '% Share (Value)', render: v => `${Math.round(v)}%` },
              ]}
              data={rows}
            />
            <p className="text-[11px] text-gray-500 mt-2">
              {d.prepost.method === 'subtype'
                ? "Prepost taken from the claim dump's Claim Sub Type column: a reimbursement claim marked pre / post hospitalisation."
                : `Prepost estimated: reimbursement claims within ${d.prepost.windowDays || 30} days of a hospitalisation for the same patient.`}
            </p>
          </Section>
        );
      })()}


      {/* ====== 3. AILMENT ANALYSIS ====== */}
      <Section id="ailment" title="Ailment Analysis" icon={Stethoscope}>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500 font-medium">View:</span>
          <button onClick={() => setAilmentView('All')} className={toggleClass('All')}>All</button>
          <button onClick={() => setAilmentView('Parents')} className={toggleClass('Parents')}>Parents Only</button>
          <button onClick={() => setAilmentView('ESC')} className={toggleClass('ESC')}>ESC</button>
        </div>

        {/* Top 10 donut - composition chart is useful here */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Card title="Top 5 Ailments by Amount" info="Diseases/conditions with highest total claim amounts">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.topAilAmt.filter(a => a.name !== 'Unknown')} cx="50%" cy="50%" outerRadius={105} innerRadius={45} dataKey="amount" nameKey="name" label={PieLabel} labelLine={false} onClick={onChartClick('ICD Disease')} style={{ cursor: 'pointer' }}>
                  {d.topAilAmt.filter(a => a.name !== 'Unknown').map((e, i) => <Cell key={i} fill={['#663399', '#e6377a', '#FF6B35', '#1B998B', '#3A86FF', '#F72585', '#7209B7', '#4CC9F0', '#F4A261', '#2EC4B6'][i % 10]} />)}
                </Pie>
                <Legend iconType="circle" iconSize={5} wrapperStyle={{ fontSize: 9, cursor: 'pointer' }} onClick={(e) => setDrillFilter('ICD Disease', e.value)} />
                <Tooltip formatter={v => formatFullIndianCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Top 5 Ailments by Count" info="Most frequently claimed diseases/conditions">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.topAilCnt.filter(a => a.name !== 'Unknown')} layout="vertical" margin={{ right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 9 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} width={120} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="count" name="Claims" fill="#e6377a" radius={[0, 3, 3, 0]} barSize={14} onClick={onChartClick('ICD Disease')} style={{ cursor: 'pointer' }}>
                  <LabelList dataKey="count" position="right" style={{ fontSize: 9, fill: '#666' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <DataTable
          title="Ailment Details"
          info="Complete list of diseases/conditions with claim counts, amounts, and average claim size"
          filename="ailments.csv"
          columns={[
            { key: 'name', header: 'Disease / Ailment' },
            { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
            { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'avgAmount', header: 'Avg Claim Amount', render: v => formatFullIndianCurrency(v) },
          ]}
          data={d.ailmentTable}
          onRowClick={(row) => setDrillFilter('ICD Disease', row.name)}
        />
      </Section>

      {/* ====== 4. MATERNITY ANALYSIS ====== */}
      <Section id="maternity" title="Maternity Analysis" icon={Baby}>
        {mat.totalMaternity === 0 ? (
          <div className="bg-gray-50 rounded-xl p-6 text-center text-sm text-gray-500">No maternity claims found</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <KPI title="Maternity Claims" value={mat.totalMaternity} icon={Baby} color="#EC4899" onClick={drill('Is Maternity Code', 'Yes')} active={isDrilled('Is Maternity Code', 'Yes')} info="Total number of maternity-related claims" />
              <KPI title="Total Amount" value={formatIndianCurrency(mat.totalAmount)} icon={IndianRupee} color="#7C3AED" info="Total claimed amount for maternity cases" />
              <KPI title="Incident Rate" value={formatPercent(mat.incidentRate)} sub="Of total claims" icon={Percent} color="#3B82F6" info="Maternity claims as a percentage of total claims" />
              <KPI title="Exceeding Limit" value={`${mat.exceedLimitCount}/${mat.totalMaternity}`} sub={mat.exceedLimitCount > 0 ? `${mat.totalMaternity - mat.exceedLimitCount} within limit` : 'All within limit'} icon={AlertTriangle} color={mat.exceedLimitCount > 0 ? '#EF4444' : '#10B981'} info="Maternity claims that exceeded the policy maternity limit" />
            </div>

            {(mat.limitSplit || []).length > 0 && (() => {
              const matCityToggle = (val) => `px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${matCityView === val ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`;
              const filteredSplit = matCityView === 'All'
                ? mat.limitSplit
                : mat.limitSplit.filter(r => r.cityType === matCityView);

              // Aggregate by delivery type when "All" (combine Metro + Non-Metro)
              const displayData = matCityView === 'All'
                ? Object.values(filteredSplit.reduce((acc, r) => {
                    if (!acc[r.deliveryType]) acc[r.deliveryType] = { deliveryType: r.deliveryType, count: 0, amount: 0, incurred: 0, avgClaim: 0, withinLimit: 0, exceedLimit: 0 };
                    acc[r.deliveryType].count += r.count;
                    acc[r.deliveryType].amount += r.amount;
                    acc[r.deliveryType].incurred += r.incurred;
                    acc[r.deliveryType].withinLimit += r.withinLimit;
                    acc[r.deliveryType].exceedLimit += r.exceedLimit;
                    acc[r.deliveryType].avgClaim = acc[r.deliveryType].count > 0 ? Math.round(acc[r.deliveryType].amount / acc[r.deliveryType].count) : 0;
                    return acc;
                  }, {}))
                : filteredSplit;

              return (
                <>
                  <div className="flex items-center gap-2 mb-3 mt-4">
                    <span className="text-xs font-semibold text-gray-700">Maternity Limit Analysis</span>
                    <div className="flex items-center gap-1 ml-3">
                      <button onClick={() => setMatCityView('All')} className={matCityToggle('All')}>All</button>
                      <button onClick={() => setMatCityView('Metro')} className={matCityToggle('Metro')}>Metro</button>
                      <button onClick={() => setMatCityView('Non-Metro')} className={matCityToggle('Non-Metro')}>Non-Metro</button>
                    </div>
                  </div>
                  <DataTable
                    title=""
                    filename="maternity_limit_split.csv"
                    columns={[
                      { key: 'deliveryType', header: 'Delivery Type' },
                      { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
                      { key: 'amount', header: 'Claimed Amount', render: v => typeof v === 'number' ? formatFullIndianCurrency(v) : v },
                      { key: 'incurred', header: 'Incurred Amount', render: v => typeof v === 'number' ? formatFullIndianCurrency(v) : v },
                      { key: 'avgClaim', header: 'Avg Claim Amount', render: v => typeof v === 'number' ? formatFullIndianCurrency(v) : v },
                      { key: 'withinLimit', header: 'Within Limit', render: v => <span className="text-emerald-600 font-medium">{v}</span> },
                      { key: 'exceedLimit', header: 'Exceeding Limit', render: v => <span className={`font-medium ${v > 0 ? 'text-red-600' : 'text-gray-500'}`}>{v}</span> },
                    ]}
                    data={displayData}
                  />
                </>
              );
            })()}
          </>
        )}
      </Section>

      {/* ====== 5. EMPLOYEE ANALYSIS ====== */}
      <Section id="employee" title="Employee Analysis" icon={UserCheck}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <KPI title="Unique Employees" value={d.empTable.length} icon={UserCheck} info="Total employees enrolled in the policy" />
          <KPI title="High Utilization >100%" value={highUtil} sub="Exceeding Sum Insured" icon={AlertTriangle} color="#EF4444" info="Employees whose claims exceed their Sum Insured coverage" />
          <KPI title="Top Claimant" value={BlurredName(d.topEmpAmt[0]?.name?.substring(0, 20))} sub={formatIndianCurrency(d.topEmpAmt[0]?.amount)} icon={IndianRupee} color="#3B82F6" onClick={d.topEmpAmt[0] ? drill('Member Name', d.topEmpAmt[0].name) : undefined} active={isDrilled('Member Name', d.topEmpAmt[0]?.name)} info="Employees with highest total claim amounts" />
          <KPI title="Most Recurring" value={BlurredName(d.topEmpCnt[0]?.name?.substring(0, 20))} sub={`${d.topEmpCnt[0]?.count || 0} claims`} icon={Hash} color="#F59E0B" onClick={d.topEmpCnt[0] ? drill('Member Name', d.topEmpCnt[0].name) : undefined} active={isDrilled('Member Name', d.topEmpCnt[0]?.name)} info="Employees with most number of claims filed" />
        </div>

        <DataTable
          title="Employee Details"
          info="Employee-wise claims breakdown - top claimants by amount and frequency"
          filename="employees.csv"
          columns={[
            { key: 'name', header: 'Employee', render: BlurredName },
            { key: 'empId', header: 'ID' },
            { key: 'sumInsured', header: 'Sum Insured', render: v => formatFullIndianCurrency(v) },
            { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
            { key: 'claimedPct', header: 'Claimed %', render: v => <span className={`font-medium text-xs px-1.5 py-0.5 rounded ${v > 100 ? 'bg-orange-100 text-orange-700' : 'text-gray-600'}`}>{formatPercent(v)}</span> },
            { key: 'utilization', header: 'Utilization %', render: v => <span className={`font-bold text-xs px-1.5 py-0.5 rounded ${v > 100 ? 'bg-red-100 text-red-700' : v > 75 ? 'bg-orange-100 text-orange-700' : 'text-gray-700'}`}>{formatPercent(v)}</span> },
          ]}
          data={d.empTable}
          onRowClick={(row) => setDrillFilter('Member Name', row.name)}
        />
      </Section>

      {/* ====== 5b. SUM INSURED UTILIZATION ====== */}
      <Section id="utilization" title="Sum Insured Utilization" icon={Shield}>
        <DataTable
          title="Utilization Band (% of Sum Insured)"
          info="How much of their Sum Insured employees have utilized - bands from 0-20% to >100%"
          filename="utilization_bands.csv"
          columns={[
            { key: 'name', header: 'Utilization Band' },
            { key: 'empIncurred', header: '# Emp (Incurred)', render: (v, row) => row.isTotal ? <strong>{v}</strong> : v },
            { key: 'pctIncurred', header: '% Emp (Incurred)' },
            { key: 'empClaimed', header: '# Emp (Claimed)', render: (v, row) => row.isTotal ? <strong>{v}</strong> : v },
            { key: 'pctClaimed', header: '% (Claimed Value)' },
          ]}
          data={d.utilBands}
        />

        <div className="mt-4" />
        <DataTable
          title="Sum Insured Wise Split"
          info="Claims distribution by Sum Insured amount - shows employee count and high utilization metrics per SI band"
          filename="si_split.csv"
          columns={[
            { key: 'sumInsured', header: 'Sum Insured', render: v => formatFullIndianCurrency(v) },
            { key: 'empCount', header: 'Employee Count', render: v => formatIndianNumber(v) },
            { key: 'claimOver80', header: 'Claim Value > 80% of SI', render: v => v },
            { key: 'claimOver100', header: 'Claim Value > 100% of SI', render: v => v },
            { key: 'incurredOver80', header: 'Incurred Value > 80% of SI', render: v => v },
          ]}
          data={d.siSplit}
        />
{/* 
        <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
          <strong>Utilization Band:</strong> SI Amount Band as % of Sum Insured<br/>
          <strong># Emp Claimed:</strong> No. of employees in this band by total claimed amount<br/>
          <strong># Emp Incurred:</strong> No. of employees in this band by total incurred amount (settled amount of paid + outstanding claims)
        </p> */}
      </Section>

      {/* ====== 6. RELATIONSHIP ANALYSIS ====== */}
      <Section id="relationship" title="Relationship Analysis" icon={Users}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {d.relations.map(r => (
            <KPI key={r.name} title={r.name} value={`${r.count} claims`} sub={`${formatIndianCurrency(r.amount)} | ${totalRelAmount > 0 ? ((r.amount / totalRelAmount) * 100).toFixed(0) : 0}%`} icon={Users} color={REL_COLORS[r.name] || '#7C3AED'} onClick={drill('PAZ_Relation', r.name)} active={isDrilled('PAZ_Relation', r.name)} info={`Claims filed by ${r.name} - count and total amount`} />
          ))}
        </div>
        <DataTable
          title="Relationship Breakdown"
          info="Claims split by relationship (Self, Spouse, Children, Parents) with amounts and percentages"
          filename="relationship.csv"
          columns={[
            { key: 'name', header: 'Relation' },
            { key: 'count', header: 'No. of Claims', render: v => formatIndianNumber(v) },
            { key: 'pctCnt', header: '% of Claims', render: v => v },
            { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'pctAmt', header: '% of Amount', render: v => v },
            { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'avgClaim', header: 'Avg Claim Amount', render: v => formatFullIndianCurrency(v) },
          ]}
          data={d.relations.map(r => ({
            ...r,
            pctCnt: totalRelCount > 0 ? formatPercent((r.count / totalRelCount) * 100) : '0%',
            pctAmt: totalRelAmount > 0 ? formatPercent((r.amount / totalRelAmount) * 100) : '0%',
            avgClaim: r.count > 0 ? Math.round(r.amount / r.count) : 0,
          }))}
          onRowClick={(row) => setDrillFilter('PAZ_Relation', row.name)}
        />
      </Section>

      {/* ====== 7. DEMOGRAPHIC ANALYSIS ====== */}
      <Section id="demographic" title="Demographic Analysis" icon={UserCircle}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Age - bar chart showing count, claimed & settled */}
          <Card title="Claims by Age Group" info="Claims distribution across age groups">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.ageGroups} barGap={2} margin={{ top: 20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="c" orientation="left" tick={{ fontSize: 9 }} />
                <YAxis yAxisId="a" orientation="right" tick={{ fontSize: 9 }} tickFormatter={v => formatIndianCurrency(v)} />
                <Tooltip content={<Tip />} />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 9 }} />
                <Bar yAxisId="c" dataKey="count" name="Claims" fill="#663399" radius={[3, 3, 0, 0]} barSize={18}>
                  <LabelList dataKey="count" position="top" style={{ fontSize: 8, fill: '#663399' }} />
                </Bar>
                <Bar yAxisId="a" dataKey="amount" name="Claimed Amt" fill="#e6377a" radius={[3, 3, 0, 0]} barSize={18} />
                <Bar yAxisId="a" dataKey="incurred" name="Incurred Amt" fill="#66339999" radius={[3, 3, 0, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          {/* Gender - donut for proportion */}
          <Card title="Gender Split" info="Claims split by gender">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.gender} cx="50%" cy="50%" outerRadius={78} innerRadius={40} dataKey="count" nameKey="name" labelLine={false} label={PieLabel} onClick={onChartClick('Gender')} style={{ cursor: 'pointer' }}>
                  {d.gender.map((e, i) => <Cell key={i} fill={GENDER_COLORS[e.name] || COLORS[i]} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10, cursor: 'pointer' }} onClick={(e) => setDrillFilter('Gender', e.value)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Amount bands & city type - tables are clearer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DataTable
            title="Claim Amount Bands"
            info="Distribution of claims across amount ranges - shows concentration of claim sizes"
            filename="amount_bands.csv"
            columns={[
              { key: 'name', header: 'Band' },
              { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
              { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
            ]}
            data={d.amountBands}
          />
          <DataTable
            title="Metro vs Non-Metro"
            info="Claims comparison between metro and non-metro cities"
            filename="city_type.csv"
            columns={[
              { key: 'name', header: 'City Type' },
              { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
              { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
              { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
            ]}
            data={d.cityType}
            onRowClick={(row) => setDrillFilter('City Type', row.name)}
          />
        </div>
      </Section>

      {/* ====== 8. HOSPITAL ANALYSIS ====== */}
      <Section id="hospital" title="Hospital Analysis" icon={Building2}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          <KPI title="Unique Hospitals" value={d.hospTable.length} icon={Building2} info="Total number of unique hospitals where claims were filed" />
          <KPI title="Unique Cities" value={d.uniqueCities || 0} icon={MapPin} color="#3B82F6" info="Total number of unique cities where claims were filed" />
          <KPI title="Top Hospital" value={d.topHospAmt[0]?.name?.substring(0, 22) || '-'} sub={formatIndianCurrency(d.topHospAmt[0]?.amount)} icon={IndianRupee} color="#10B981" onClick={d.topHospAmt[0] ? drill('Hospital Name', d.topHospAmt[0].name) : undefined} active={isDrilled('Hospital Name', d.topHospAmt[0]?.name)} info="Hospitals with highest total claim amounts" />
          <KPI title="Top City" value={d.hospCity[0]?.name || '-'} sub={`${d.hospCity[0]?.count || 0} claims | ${formatIndianCurrency(d.hospCity[0]?.amount)}`} icon={MapPin} color="#e6377a" onClick={d.hospCity[0] ? drill('Hospital City', d.hospCity[0].name) : undefined} active={isDrilled('Hospital City', d.hospCity[0]?.name)} info="City with the most claims by amount" />
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Network Split</p>
            {(d.hospType || []).map(h => (
              <p key={h.name} className="text-xs text-gray-700 leading-5">{h.name} - <span className="font-bold">{h.count}</span> claims - {formatIndianCurrency(h.amount)}</p>
            ))}
          </div>
        </div>

        <Card title="Top 10 Hospitals by Amount" info="Hospitals with highest total claim amounts" className="mb-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={d.topHospAmt} layout="vertical" margin={{ right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => formatIndianCurrency(v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} width={140} />
              <Tooltip content={<Tip />} />
              <Bar dataKey="amount" name="Amount" fill="#663399" radius={[0, 3, 3, 0]} barSize={16} onClick={onChartClick('Hospital Name')} style={{ cursor: 'pointer' }}>
                <LabelList dataKey="amount" position="right" style={{ fontSize: 8, fill: '#666' }} formatter={v => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <DataTable
          title="Hospital Details"
          info="Complete hospital-wise claims data with city, network type, counts, and amounts"
          filename="hospitals.csv"
          columns={[
            { key: 'name', header: 'Hospital' },
            { key: 'city', header: 'City' },
            { key: 'type', header: 'Network' },
            { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
            { key: 'amount', header: 'Claimed', render: v => formatFullIndianCurrency(v) },
            { key: 'incurred', header: 'Incurred', render: v => formatFullIndianCurrency(v) },
            { key: 'avgAmount', header: 'Avg Claim', render: v => formatFullIndianCurrency(v) },
          ]}
          data={d.hospTable}
          onRowClick={(row) => setDrillFilter('Hospital Name', row.name)}
        />

        <div className="mt-4" />
        <DataTable
          title="City-wise Hospital Claims"
          info="Claims aggregated by city showing hospital count and total claim amounts"
          filename="hospitals_by_city.csv"
          columns={[
            { key: 'name', header: 'City' },
            { key: 'count', header: 'No. of Claims', render: v => formatIndianNumber(v) },
            { key: 'amount', header: 'Claimed Amount', render: v => formatFullIndianCurrency(v) },
            { key: 'incurred', header: 'Incurred Amount', render: v => formatFullIndianCurrency(v) },
          ]}
          data={d.hospCity}
          onRowClick={(row) => setDrillFilter('Hospital City', row.name)}
        />
      </Section>

      {d.duration && (() => {
        const t = d.duration.totals;
        const buckets = d.duration.buckets || [];
        return (
          <Section id="duration" title="Hospitalization Duration" icon={Clock}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <KPI title="Total Hospitalizations" value={formatIndianNumber(t.hospitalizations)} icon={Building2} />
              <KPI title="Total Days" value={formatIndianNumber(t.totalDays)} icon={Clock} color="#3B82F6" />
              <KPI title="Avg LOS (Days)" value={Math.round(t.avgLos || 0)} sub={`${(t.avgLos || 0).toFixed(1)} exact`} icon={Timer} color="#663399" />
              <KPI title="Median LOS (Days)" value={t.medianLos ?? '—'} icon={Activity} color="#10B981" />
              <KPI title="Max LOS (Days)" value={t.maxLos ?? '—'} icon={Clock} color="#F59E0B" />
            </div>
            <DataTable
              title="By Relation"
              filename="stay_by_relation.csv"
              columns={[
                { key: 'relation', header: 'Relation' },
                { key: 'totalDays', header: 'Total Days', render: v => formatIndianNumber(v) },
                { key: 'hospitalizations', header: 'Total Hospitalization', render: v => formatIndianNumber(v) },
                ...buckets.map(b => ({ key: b, header: b, render: v => formatIndianNumber(v || 0) })),
              ]}
              data={d.duration.byRelation || []}
            />
            <div className="mt-4">
              <DataTable
                title="Top Ailments by Hospital Days"
                filename="stay_by_ailment.csv"
                columns={[
                  { key: 'ailment', header: 'Top Ailments' },
                  { key: 'hospitalizations', header: 'Total Hospitalization', render: v => formatIndianNumber(v) },
                  { key: 'totalDays', header: 'Total Days', render: v => formatIndianNumber(v) },
                  { key: 'avgLos', header: 'Avg LOS (Days)', render: v => Math.round(v || 0) },
                  { key: 'medianLos', header: 'Median LOS (Days)', render: v => v ?? '—' },
                  { key: 'maxLos', header: 'Max LOS (Days)', render: v => v ?? '—' },
                ]}
                data={d.duration.topAilments || []}
              />
            </div>
            <p className="text-[11px] text-gray-500 mt-2">A hospitalization is one stay — the same employee, patient, admission and discharge dates and relation — however many claims it was billed in. Length of stay = discharge − admission + 1 days.</p>
          </Section>
        );
      })()}


      {/* ====== 9. REJECTION ANALYSIS ====== */}
      <Section id="rejection" title="Rejection Analysis" icon={XCircle}>
        {rej.totalRejected === 0 ? (
          <div className="bg-emerald-50 rounded-xl p-6 text-center text-sm text-emerald-700 font-medium">No rejected claims!</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <KPI title="Rejected Claims" value={rej.totalRejected} icon={XCircle} color="#EF4444" onClick={drill('Claim Status', 'Rejected')} active={isDrilled('Claim Status', 'Rejected')} info="Total number of claims rejected by the insurer" />
              <KPI title="Rejection Rate" value={formatPercent(rej.rejectionRate)} icon={Percent} color="#F59E0B" info="Percentage of claims rejected out of total closed claims" />
              <KPI title="Rejected Amount" value={formatIndianCurrency(rej.rejectedAmount)} icon={IndianRupee} color="#EF4444" info="Total claimed amount of rejected claims" />
              <KPI title="Top Category" value={rej.byCategory[0]?.name?.substring(0, 22) || '-'} sub={`${rej.byCategory[0]?.count || 0} claims`} icon={FileWarning} color="#7C3AED" onClick={rej.byCategory[0] ? drill('Rejection Category', rej.byCategory[0].name) : undefined} active={isDrilled('Rejection Category', rej.byCategory[0]?.name)} info="Most common reasons for claim rejections" />
            </div>

            {/* Rejection Treemap - size = claims count */}
            <Card title="Rejection Categories (by Claims)" info="Most common reasons for claim rejections" className="mb-4">
              <ResponsiveContainer width="100%" height={220}>
                {(() => {
                  const colors = ['#EF4444', '#F97316', '#F59E0B', '#EC4899', '#8B5CF6', '#6366F1', '#3B82F6'];
                  const treemapData = (rej.byCategory || []).slice(0, 7).map((c, i) => ({
                    name: c.name?.length > 25 ? c.name.substring(0, 25) + '...' : c.name,
                    fullName: c.name,
                    size: c.count,
                    amount: c.amount,
                    fill: colors[i % colors.length],
                  }));
                  const CustomContent = ({ x, y, width, height, name, size, fill }) => {
                    if (width < 30 || height < 25) return null;
                    return (
                      <g>
                        <rect x={x} y={y} width={width} height={height} rx={6} fill={fill} stroke="#fff" strokeWidth={2} style={{ cursor: 'pointer' }} />
                        <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle" fill="#fff" fontSize={width < 80 ? 8 : 10} fontWeight={600}>{name}</text>
                        <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={9}>{size} claims</text>
                      </g>
                    );
                  };
                  return (
                    <Treemap data={treemapData} dataKey="size" nameKey="name" content={<CustomContent />}>
                      <Tooltip formatter={(v, name, props) => [`${v} claims, ${formatIndianCurrency(props.payload.amount)}`, props.payload.fullName || name]} />
                    </Treemap>
                  );
                })()}
              </ResponsiveContainer>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <DataTable
                title="By Category"
                info="Rejection reasons grouped by category with claim count and rejected amounts"
                filename="rejection_categories.csv"
                columns={[
                  { key: 'name', header: 'Category' },
                  { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
                  { key: 'amount', header: 'Amount', render: v => formatFullIndianCurrency(v) },
                ]}
                data={rej.byCategory}
                onRowClick={(row) => setDrillFilter('Rejection Category', row.name)}
              />
              <DataTable
                title="By Reason"
                info="Detailed rejection reasons with individual claim counts and amounts"
                filename="rejection_reasons.csv"
                columns={[
                  { key: 'name', header: 'Reason' },
                  { key: 'count', header: 'Claims', render: v => formatIndianNumber(v) },
                  { key: 'amount', header: 'Amount', render: v => formatFullIndianCurrency(v) },
                ]}
                data={rej.byReason}
                onRowClick={(row) => setDrillFilter('Rejection Reason', row.name)}
              />
            </div>

            <DataTable
              title="Rejected Claims Detail"
              info="Individual rejected claims with patient details, diagnosis, and rejection reason"
              filename="rejected_claims.csv"
              columns={[
                { key: 'Claim Number', header: 'Claim #' },
                { key: 'Member Name', header: 'Employee', render: BlurredName },
                { key: 'Claim Type', header: 'Type' },
                { key: '_claimedAmount', header: 'Amount', render: v => formatFullIndianCurrency(v) },
                { key: 'Rejection Category', header: 'Category' },
                { key: 'Rejection Reason', header: 'Reason' },
              ]}
              data={rej.claims}
              onRowClick={(row) => setDrillFilter('Rejection Category', row['Rejection Category'])}
            />
          </>
        )}
      </Section>

      {d.accidental && (
        <Section id="accidental" title="Accidental Claim Summary" icon={AlertTriangle}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            <KPI title="Accidental Claims" value={formatIndianNumber(d.accidental.claims)} icon={AlertTriangle} color="#EF4444" />
            <KPI title="Settled" value={formatIndianNumber(d.accidental.settled)} icon={CheckCircle} color="#10B981" />
            <KPI title="Rejected" value={formatIndianNumber(d.accidental.rejected)} icon={XCircle} color="#e6377a" />
            <KPI title="Claimed Amount" value={formatIndianCurrency(d.accidental.claimedAmount)} icon={IndianRupee} color="#663399" />
            <KPI title="Incurred Amount" value={formatIndianCurrency(d.accidental.incurredAmount)} icon={TrendingUp} color="#3B82F6" />
          </div>
          <p className="text-[11px] text-gray-500">Accidental claims are those with an ICD code starting S, T, V, W, X or Y — fractures, burns, wounds, falls, injuries to body regions, poisonings and other external causes. Incurred = settled amount on claims that aren't rejected.</p>
        </Section>
      )}


      {/* ====== CLAIMS DETAIL TABLE ====== */}
      {d.claimsDetail && d.claimsDetail.length > 0 && (
        <Section id="claims-detail" title="Claims Detail" icon={FileText}>
          <DataTable
            title="All Claims"
            info="Complete claims register - all individual claims with full details"
            filename="claims_detail.csv"
            columns={[
              { key: 'Claim Number', header: 'Claim #' },
              { key: 'Claim Type', header: 'Type' },
              { key: 'Claim Status', header: 'Status' },
              { key: 'Claimed Amount', header: 'Claimed Amt', render: v => formatFullIndianCurrency(v) },
              { key: 'Settled Amount', header: 'Incurred Amt', render: v => formatFullIndianCurrency(v) },
              { key: 'Employee ID', header: 'Emp ID' },
              { key: 'Member Name', header: 'Employee', render: BlurredName },
              { key: 'Insured Person/Patient Name', header: 'Patient', render: BlurredName },
              { key: 'PAZ_Relation', header: 'Relation' },
              { key: 'Hospital Name', header: 'Hospital' },
              { key: 'Hospital City', header: 'City' },
              { key: 'ICD Disease', header: 'Disease' },
              { key: 'Claim Intimation Date', header: 'Intimation' },
              { key: 'Claim Settlement Date', header: 'Settlement' },
              { key: 'Date of Admission', header: 'DOA' },
              { key: 'Date of Discharge', header: 'DOD' },
              { key: 'Age', header: 'Age' },
              { key: 'Gender', header: 'Gender' },
              { key: 'Rejection Category', header: 'Rejection Category' },
              { key: 'Rejection Reason', header: 'Rejection Reason' },
            ]}
            data={d.claimsDetail}
          />
        </Section>
      )}

      {/* ====== RENEWAL PREMIUM ESTIMATOR ====== */}
      {d.renewalPremium && (() => {
        const rp = d.renewalPremium;
        const fmtL = (v) => {
          if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
          if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
          return `₹${v.toLocaleString('en-IN')}`;
        };
        const lives = rpLivesOverride ?? rp.livesForRenewal;
        const totalAgg = Math.round(rp.perLifeAggressive * lives);
        const totalCon = Math.round(rp.perLifeConservative * lives);
        return (
          <Section id="renewal-premium" title="Expected Renewal Premium" icon={Calculator}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Parameters card */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-800 mb-3">Estimation Parameters</p>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-xs"><span className="text-gray-500">ICR (Annualized)</span><span className="font-semibold">{rp.icrAnnualized}%</span></div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Annualized Claims</span><span className="font-semibold">{fmtL(rp.annualizedClaims)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Days to Expiry</span><span className="font-semibold">{rp.daysFromExpiry} days</span></div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Premium (Inception)</span><span className="font-semibold">{fmtL(rp.premiumInception)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-gray-500">Lives (Inception)</span><span className="font-semibold">{rp.livesInception}</span></div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Lives for Renewal</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        key={`rp-lives-${rpLivesOverride ?? 'default'}`}
                        defaultValue={lives}
                        onBlur={e => {
                          const v = parseInt(e.target.value);
                          if (v > 0 && v !== rp.livesForRenewal) setRpLivesOverride(v);
                          else if (v === rp.livesForRenewal) setRpLivesOverride(null);
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        className="w-16 text-right font-semibold text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                      />
                      {rpLivesOverride != null && <button onClick={() => setRpLivesOverride(null)} className="text-gray-400 hover:text-gray-600 text-[10px]" title="Reset to original">↺</button>}
                    </div>
                  </div>
                </div>
                <p className="text-[9px] text-purple-400 mt-3 italic">You can edit Lives for Renewal to see how premium changes.</p>
              </div>

              {/* Range visualization - spans 2 cols */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-800 mb-4">Estimated Premium Range</p>
                <div className="relative">
                  <div className="h-16 rounded-xl bg-gradient-to-r from-emerald-50 via-purple-50 to-amber-50 border border-gray-100 flex items-center justify-between px-6">
                    <div>
                      <p className="text-xl font-bold text-emerald-700">{fmtL(totalAgg)}</p>
                      <p className="text-[10px] text-emerald-500 font-medium">Aggressive</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-gray-400 font-medium">to</span>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-amber-700">{fmtL(totalCon)}</p>
                      <p className="text-[10px] text-amber-500 font-medium">Conservative</p>
                    </div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-purple-400 to-amber-400" style={{width: '100%'}} />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
                    <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider">Aggressive (Per Life)</p>
                    <p className="text-lg font-bold text-emerald-700 mt-1">₹{rp.perLifeAggressive.toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{rp.method === 'icr_based' ? 'Lower loadings (IBNR 2%, Inflation 5%)' : `Discount: ${rp.discountAggressive}%`}</p>
                  </div>
                  <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
                    <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wider">Conservative (Per Life)</p>
                    <p className="text-lg font-bold text-amber-700 mt-1">₹{rp.perLifeConservative.toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{rp.method === 'icr_based' ? 'Higher loadings (IBNR 3%, Inflation 6%)' : `Discount: ${rp.discountConservative}%`}</p>
                  </div>
                </div>
                <p className="text-[9px] text-gray-400 mt-3 text-center">Estimates based on claims experience. Actual renewal may vary based on insurer assessment and market conditions.</p>
              </div>
            </div>
          </Section>
        );
      })()}

        {/* ===== POLICY BENEFITS ===== */}
        <Section id="policy-benefits" title="Current Policy Benefits" icon={Shield}>
          {policyBenefitsLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 h-32">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-100 rounded w-full" />
                    <div className="h-2 bg-gray-100 rounded w-4/5" />
                    <div className="h-2 bg-gray-100 rounded w-3/5" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!policyBenefitsLoading && !policyBenefits && !policyUploading && (
            <div className="text-center py-6">
              <div
                className="border-2 border-dashed border-purple-200 rounded-xl p-6 max-w-sm mx-auto cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-all"
                onClick={() => document.getElementById('dashboard-policy-input').click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400', 'bg-purple-50/30'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); const f = e.dataTransfer.files[0]; if (f) handleDashboardPolicyUpload(f); }}
              >
                <Upload size={24} className="text-purple-300 mx-auto mb-2" />
                <p className="text-xs font-medium text-gray-600 mb-1">Upload Policy Copy</p>
                <p className="text-[10px] text-gray-400">Drag & drop or click to upload PDF - enables benchmarking & recommendations</p>
              </div>
              <input id="dashboard-policy-input" type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleDashboardPolicyUpload(e.target.files[0]); }} />
            </div>
          )}
          {policyUploading && (
            <div className="text-center py-8">
              <Loader2 size={24} className="text-purple-500 animate-spin mx-auto mb-2" />
              <p className="text-xs font-medium text-purple-600">Extracting policy benefits...</p>
              <p className="text-[10px] text-gray-400 mt-1">This may take 30–60 seconds</p>
            </div>
          )}
          {policyBenefits && Object.keys(policyBenefits).length > 0 && (() => {
              const mergeGroups = [
                { title: 'Sum Insured & Family Structure', icon: '💰', border: 'border-purple-400', text: 'text-purple-700', sections: ['Sum Insured', 'Family Structure'] },
                { title: 'Maternity Benefits', icon: '🤰', border: 'border-pink-400', text: 'text-pink-700', sections: ['Maternity'] },
                { title: 'Room Rent & Co-Pay', icon: '🏥', border: 'border-red-400', text: 'text-red-700', sections: ['Room Rent', 'CoPay'] },
                { title: 'Other GHI Benefits', icon: '🛡️', border: 'border-violet-400', text: 'text-violet-700', sections: ['Other GHI'] },
                { title: 'Cancer Care & Modern Treatments', icon: '🎗️', border: 'border-rose-400', text: 'text-rose-700', sections: ['Cancer Care'] },
                { title: 'Disease Wise Capping', icon: '📋', border: 'border-fuchsia-400', text: 'text-fuchsia-700', sections: ['Disease Capping'] },
              ];
              const grouped = {};
              Object.entries(policyBenefits).forEach(([key, val]) => {
                const sec = val.section || 'Other';
                if (!grouped[sec]) grouped[sec] = [];
                grouped[sec].push({ key, ...val });
              });
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mergeGroups.map(group => {
                    const allFields = group.sections.flatMap(s => grouped[s] || []);
                    if (allFields.length === 0) return null;
                    return (
                      <div key={group.title} className={`bg-white rounded-xl border-l-4 ${group.border} shadow-sm p-4`}>
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                          <span className="text-base">{group.icon}</span>
                          <h4 className={`text-xs font-bold ${group.text} uppercase tracking-wider`}>{group.title}</h4>
                        </div>
                        <div className="space-y-1.5">
                          {allFields.map(f => {
                            const value = f.formatted || f.raw || '-';
                            const isNotCovered = value.toLowerCase().includes('not covered') || value.toLowerCase().includes('not specified');
                            return (
                              <div key={f.key} className="flex justify-between items-start gap-2">
                                <span className="text-[11px] text-gray-500 min-w-0">{f.label}</span>
                                <span className={`text-[11px] font-medium text-right ${isNotCovered ? 'text-gray-400' : 'text-gray-900'}`}>{value}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </Section>

        {/* ====== POLICY BENCHMARKING ====== */}
        {insights && insights.benchmark_comparisons && insights.benchmark_comparisons.length > 0 && (
          <Section id="policy-benchmarking" title="Policy Benchmarking" icon={Sparkles}>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="text-left px-4 py-2 font-semibold text-purple-700">Benefit</th>
                    <th className="text-left px-4 py-2 font-semibold text-purple-700">Your Policy</th>
                    <th className="text-left px-4 py-2 font-semibold text-purple-700">Top 25%</th>
                    <th className="text-left px-4 py-2 font-semibold text-purple-700">Top 10%</th>
                    <th className="text-left px-4 py-2 font-semibold text-purple-700">Paz Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.benchmark_comparisons.map((bm, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 font-medium text-gray-800">{bm.benefit}</td>
                      <td className="px-4 py-2 text-gray-700">{bm.current_value}</td>
                      <td className="px-4 py-2 text-gray-500">{bm.benchmark_top25}</td>
                      <td className="px-4 py-2 text-gray-500">{bm.benchmark_top10}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-start gap-1.5">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${
                            bm.assessment === 'exceeds' ? 'bg-green-500' :
                            bm.assessment === 'meets' ? 'bg-blue-500' :
                            'bg-red-500'
                          }`} />
                          <span className={`text-[10px] ${
                            bm.assessment === 'exceeds' ? 'text-green-700' :
                            bm.assessment === 'meets' ? 'text-blue-700' :
                            'text-red-700'
                          }`}>
                            {bm.recommendation || (bm.assessment === 'exceeds' ? `${bm.benefit} is above industry Top 25% - strong position for renewals` : bm.assessment === 'meets' ? `${bm.benefit} meets industry standards - review during renewal for enhancement` : `${bm.benefit} is below industry Top 25% (${bm.benchmark_top25}) - consider increasing to stay competitive`)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

    </div>
  );
};

export default SinglePageDashboard;
